import React from "react"
import { graphql } from "@apollo/client/react/hoc"
import gql from "graphql-tag"
import "./InspectionDocumentsSection.css"
import { cleanDocumentUrl } from "../../Material/DocumentModal/DocumentsListNew"
import { isImage, isPdf, isVideo } from "../../Material/DocumentModal/utils"
import DatetimeRenderer from "../../Material/DatetimeRenderer"
import toTitleCase from "../../Functions/toTitleCase"

const AllCarPicturesQuery = gql`
    query AllCarPictures($incidentId: ID!) {
        allCarPictures(incidentId: $incidentId) {
        edges {
            node {
                id
                dateAdded
                pictureUrl
                pictureType
                formType
            }
        }
        }
    }
`

const renderPreview = (pictureUrl, pictureType, idx) => {
    if (isImage(pictureUrl)) {
        return <img className="inspection-documents-thumb-media" src={pictureUrl} alt={pictureType || "Inspection"} />
    }
    if (isVideo(pictureUrl, pictureType)) {
        return <video className="inspection-documents-thumb-media" src={pictureUrl} playsInline muted preload="metadata" />
    }
    if (isPdf(pictureUrl, pictureType)) {
        return <iframe scrolling="no" className="inspection-documents-thumb-media" src={pictureUrl} title={`inspection-iframe-${idx}`} />
    }
    return (
        <div className="inspection-documents-thumb-fallback">
            <i className="fa fa-file-text-o" aria-hidden="true" />
        </div>
    )
}

const InspectionDocumentsSection = ({ pictures = [], picturesLoading = false, loading = false, enableSelection = true, selectedDocs = [], onToggleSelect, onPreview, }) => {
    if (!pictures.length) {
        if (picturesLoading) return null

        return (
            <div className="documents-empty-state-wrap">
                <div className="documents-empty-state">
                    <span className="documents-empty-state__icon">
                        <i className="fa fa-camera" aria-hidden="true" />
                    </span>
                    <h3 className="documents-empty-state__title mb-0">No Documents Yet</h3>
                    <p className="documents-empty-state__description">
                        Upload documents or request missing ones to keep this section complete.
                    </p>
                </div>
            </div>
        )
    }

    const inspectionDate = pictures[0]?.node?.dateAdded || null
    const sourceLabel = pictures[0]?.node?.formType || "VGM Admin App"

    return (
        <div className="inspection-documents">
            <div className="inspection-documents-header">
                <div className="inspection-documents-heading">
                    <div className="inspection-documents-title">{sourceLabel}</div>
                    <div className="inspection-documents-date">
                        {inspectionDate && <DatetimeRenderer datetime={inspectionDate} />}
                    </div>
                </div>
            </div>
            <div className="inspection-documents-gallery">
                {pictures.map((pic, idx) => {
                    const picture = pic.node
                    const pictureUrl = cleanDocumentUrl(picture.pictureUrl)
                    const pictureType = picture.pictureType || "Inspection Photo"
                    const isSelected = !!selectedDocs.find((doc) => doc.id === picture.id)

                    return (
                        <div
                            role="button"
                            tabIndex={0}
                            className={`inspection-documents-thumb${loading ? " is-disabled" : ""}`}
                            key={picture.id}
                            onClick={() => !loading && onPreview(pictures, idx)}
                            onKeyDown={(e) => {
                                if (!loading && (e.key === "Enter" || e.key === " ")) {
                                    e.preventDefault()
                                    onPreview(pictures, idx)
                                }
                            }}
                        >
                            {enableSelection && isImage(pictureUrl) && (
                                <input
                                    type="checkbox"
                                    className="inspection-documents-checkbox"
                                    checked={isSelected}
                                    disabled={loading}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => onToggleSelect({
                                        id: picture.id,
                                        pictureUrl: picture.pictureUrl,
                                        documentType: { typeName: pictureType },
                                    })}
                                />
                            )}
                            {renderPreview(pictureUrl, picture.pictureType, idx)}
                            <span className="inspection-documents-thumb-label">{toTitleCase(picture.pictureType || "Other")}</span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default graphql(AllCarPicturesQuery, {
    skip: ({ incidentId }) => !incidentId,
    options: ({ incidentId }) => ({
        variables: { incidentId },
        fetchPolicy: "network-only"
    }),
    props: ({ data: { allCarPictures, loading } }) => ({
        pictures: allCarPictures?.edges || [],
        picturesLoading: loading,
    }),
})(InspectionDocumentsSection)
